import { cn } from "@/lib/utils";
import {
  Envelope,
  Globe,
  InstagramLogo,
  MessengerLogo,
  PhoneCall,
  WhatsappLogo,
  type Icon,
} from "@phosphor-icons/react";
import type { CSSProperties } from "react";
import type { ChannelKind } from "./channels";

/**
 * A channel's logo: a white glyph on a tile in the channel's own colours, so
 * that one can be picked out of the list at a glance. The Meta ones wear their
 * brands' colours; the three that belong to nobody (a phone line, a web chat, a
 * mailbox) get one of ours each, none of them a brand's — an envelope on red
 * would read as Gmail.
 */
const LOGOS: Record<ChannelKind, { icon: Icon; background: string }> = {
  phone: { icon: PhoneCall, background: "#2563EB" },
  web: { icon: Globe, background: "#7C3AED" },
  email: { icon: Envelope, background: "#F59E0B" },
  whatsapp: { icon: WhatsappLogo, background: "#25D366" },
  messenger: {
    icon: MessengerLogo,
    background:
      "linear-gradient(135deg, #00B2FF 0%, #A033FF 55%, #FF5280 100%)",
  },
  instagram: {
    icon: InstagramLogo,
    background:
      "radial-gradient(circle at 30% 107%, #FDF497 0%, #FDF497 5%, #FD5949 45%, #D6249F 60%, #285AEB 90%)",
  },
};

const SIZES = {
  sm: { tile: "h-6 w-6 rounded-md", glyph: "h-3.5 w-3.5" },
  md: { tile: "h-10 w-10 rounded-xl", glyph: "h-6 w-6" },
} as const;

export const ChannelLogo = ({
  kind,
  size = "md",
  className,
}: {
  kind: ChannelKind;
  size?: keyof typeof SIZES;
  className?: string;
}) => {
  const { icon: Glyph, background } = LOGOS[kind];
  const style: CSSProperties = { background };
  return (
    <span
      aria-hidden
      style={style}
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-white shadow-sm",
        SIZES[size].tile,
        className,
      )}
    >
      <Glyph className={SIZES[size].glyph} weight="fill" />
    </span>
  );
};
